import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { getMarketplaceSourceTabFromTabName, MARKETPLACE_TAB } from 'constants/marketplace';
import { getListContextFromSearchListKey } from 'helpers/search';
import BuysideSearchService from 'services/timedSales/buyside/search.service';
import { getDraftFilterState } from 'store/slices/buyers/filters/filtersSelectors';
import { getDefaultParams, getDefaultSortingParams, getSanitizedFilterParams } from './helpers';
import { getAllowedUrlParams } from 'helpers/url/utmParams';

const getCatchSavedSearch = async (savedSearchId) => {
	try {
		// if saved search cannot be fetched we should continue with no issue, we do not block filters due to this issue.
		const savedSearchForId = await BuysideSearchService.instance.getSavedSearch(savedSearchId);
		return savedSearchForId;
	} catch {}
};

// TODO: this code should be used for a short time, remove before september 2025
const reWriteDeprecatedParams = (params) => {
	const { dealer_id, dealer_ids, ...rest } = params;
	return {
		...rest,
		dealer_ids: dealer_ids || dealer_id,
	};
};

export const loadFiltersForTab = createAsyncThunk(
	'loadFiltersForTab',
	async ({ context, searchListKey, enableFilters, initialUrlQueryParam }, thunkAPI) => {
		if (!enableFilters) {
			return {
				sections: [],
				hotfilters: [],
				sortingOptions: [],
				defaultParams: {},
				params: {},
			};
		}
		const requests = [];
		let savedSearchParams = {};

		const sourceTab = getMarketplaceSourceTabFromTabName(searchListKey);

		// this is for tbd_187929_unified_marketplace_react === 'mixed'
		const saleType = thunkAPI.getState().buyerVehicles.saleType;
		requests.push(BuysideSearchService.instance.getFilters({ context: saleType || context, sourceTab }));
		// requests.push(BuysideSearchService.instance.getFilters({ context, sourceTab }));

		// all tabs call this thunk to initialize filters but only the one that is on URL will pre-populate store from url query params.
		const shouldLoadParamsFromUrl =
			(initialUrlQueryParam?.tab && initialUrlQueryParam?.tab === searchListKey) ||
			(!initialUrlQueryParam?.tab && searchListKey === MARKETPLACE_TAB.marketplace);

		if (shouldLoadParamsFromUrl) {
			if (initialUrlQueryParam?.saved_search_id) {
				requests.push(getCatchSavedSearch(initialUrlQueryParam?.saved_search_id));
			}
		}
		const [sectionsResponse, savedSearchFromId] = await Promise.all(requests);

		const { sections, sort_strategies, hotfilters, tabs } = sectionsResponse.data;
		if (savedSearchFromId) savedSearchParams = savedSearchFromId.params;

		const defaultParams = { ...getDefaultParams(sections), ...getDefaultSortingParams(sort_strategies) };
		// here search params should be initit as filters

		return {
			sections,
			sortingOptions: sort_strategies,
			defaultParams,
			hotfilters,
			tabs,
			params:
				shouldLoadParamsFromUrl ?
					getSanitizedFilterParams(
						{ ...savedSearchParams, ...reWriteDeprecatedParams(initialUrlQueryParam) },
						{ ...defaultParams, ...getAllowedUrlParams(initialUrlQueryParam) },
						sections,
					)
				:	defaultParams,
		};
	},
);

// It returns how many vehicles match with draft filter state
export const searchPreview = createAsyncThunk('searchPreview', async ({ searchListKey }, thunkAPI) => {
	const source = axios.CancelToken.source();
	const { signal, getState } = thunkAPI;
	const state = getState();
	signal.addEventListener('abort', () => {
		source.cancel();
	});
	const draftState = getDraftFilterState(state, searchListKey);
	const filters = {
		...draftState,
		source_tab: getMarketplaceSourceTabFromTabName(searchListKey),
		context: getListContextFromSearchListKey(searchListKey),
		sale_types: state.buyerVehicles.saleType || draftState.sale_types,
	};
	const { data } = await BuysideSearchService.instance.searchPreview(filters, source.token);
	const totalMatches = data?.metadata?.total_count;
	const changedFilters = data?.applied_filters?.filters;

	return {
		totalMatches,
		changedFilters,
	};
});
